import axios from 'axios'
import { StorageService } from './storage';
import { SECURITY_CONFIG } from './security';
import { createForbiddenRedirectGuard, diagnoseConnectivity } from 'src/composables/useConnectivityDiagnosis';
import { setApiState } from 'src/composables/useNetworkStatus';

axios.defaults.baseURL = process.env.API_PATH;
//axios.defaults.headers.post['Content-Type']  = 'application/x-www-form-urlencoded';

axios.defaults.headers.post['Content-Type']  = 'application/json';

const appVersion = process.env.APP_VERSION || process.env.npm_package_version || 'unknown';

// Set Accept-Language header
let currentLocale = StorageService.getLocale() || 'pt-PT';
axios.defaults.headers.common['Accept-Language'] = currentLocale;

// Set Firewall Bypass Header
axios.defaults.headers.common[SECURITY_CONFIG.FIREWALL_HEADER] = SECURITY_CONFIG.FIREWALL_HASH;
axios.defaults.headers.common['X-App-Version'] = appVersion;

// If token exists set header
let token = StorageService.getToken();
if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
}

let connectivityInterceptorsConfigured = false;
let connectivityHooks = {
    onRequestStart: null,
    onRejectedRoute: null
};

const forbiddenRedirectGuard = createForbiddenRedirectGuard({
    requiredConsecutive403: 2,
    windowMs: 20000
});

const networkReportState = {
    lastSentAtByKey: new Map(),
    cooldownMs: 300000
};

function canReportConnectivity() {
    return !!StorageService.getToken();
}

function canQueueConnectivityReport(alertType) {
    if (alertType === 'login-offline') {
        return true;
    }
    return canReportConnectivity();
}

function canSendNetworkReport(reportKey) {
    const now = Date.now();
    const lastSentAt = networkReportState.lastSentAtByKey.get(reportKey) || 0;
    if ((now - lastSentAt) < networkReportState.cooldownMs) {
        return false;
    }
    networkReportState.lastSentAtByKey.set(reportKey, now);
    return true;
}

async function reportConnectivityEvent(alertType, diagnosis, error) {
    if (!canQueueConnectivityReport(alertType)) {
        return;
    }

    const requestUrl = error?.config?.url || 'unknown';
    const requestMethod = (error?.config?.method || 'get').toUpperCase();

    if (alertType === 'connection') {
        const reportKey = `${requestMethod}:${requestUrl}:${appVersion}`;
        if (!canSendNetworkReport(reportKey)) {
            return;
        }
    }

    const payload = {
        alertType,
        statusCode: Number(error?.response?.status || 0),
        requestUrl,
        requestMethod,
        appVersion,
        publicIp: diagnosis?.publicIp || null,
        timestamp: new Date().toISOString(),
        diagnosis: diagnosis || {},
        requiresAuth: alertType !== 'login-offline'
    };

    // Guardar na fila — será enviado quando a rede for restabelecida
    StorageService.savePendingConnectivityReport(payload);
    console.log('[connectivity] evento guardado na fila:', payload.requestMethod, payload.requestUrl, '| fila:', StorageService.getPendingConnectivityReports().length);
}

function queueLoginOfflineEvent() {
    const requestUrl = '/auth/login';
    const requestMethod = 'POST';
    const reportKey = `${requestMethod}:${requestUrl}:${appVersion}`;
    if (!canSendNetworkReport(reportKey)) {
        return;
    }
    const payload = {
        alertType: 'login-offline',
        statusCode: 0,
        requestUrl,
        requestMethod,
        appVersion,
        publicIp: null,
        timestamp: new Date().toISOString(),
        diagnosis: {
            appVersion,
            has403: false,
            networkError: true,
            browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : false,
            hasInternet: false,
            publicIp: null,
            isOffline: true,
            shouldRedirectRejected: false
        },
        requiresAuth: false
    };
    StorageService.savePendingConnectivityReport(payload);
    console.log('[connectivity] login offline guardado na fila.');
}

async function flushPendingConnectivityReports() {
    const pending = StorageService.getPendingConnectivityReports();
    if (!pending.length) return;

    console.log('[connectivity] rede restaurada — enviando', pending.length, 'relatório(s) pendente(s)');
    const remaining = [];

    for (const payload of pending) {
        if (payload?.requiresAuth && !canReportConnectivity()) {
            remaining.push(payload);
            continue;
        }

        try {
            await axios.post('/auth/report-connectivity', payload, {
                __skipConnectivityHandling: true,
                timeout: 6000
            });
            console.log('[connectivity] relatório enviado:', payload.requestMethod, payload.requestUrl, '| timestamp da falha:', payload.timestamp);
        } catch (err) {
            remaining.push(payload);
            console.warn('[connectivity] falha ao enviar relatório pendente:', err?.message || err);
        }
    }

    StorageService.setPendingConnectivityReports(remaining);
}

export const ApiService = {
    queueLoginOfflineEvent,
    setupConnectivityInterceptors(hooks = {}) {
        connectivityHooks = {
            ...connectivityHooks,
            ...hooks
        };

        if (connectivityInterceptorsConfigured) {
            return;
        }

        connectivityInterceptorsConfigured = true;

        axios.interceptors.request.use(
            (config) => {
                if (!config?.__skipConnectivityHandling && typeof connectivityHooks.onRequestStart === 'function') {
                    connectivityHooks.onRequestStart();
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        axios.interceptors.response.use(
            (response) => {
                if (response?.config?.__skipConnectivityHandling) {
                    return response;
                }
                setApiState('online');
                forbiddenRedirectGuard.reset();
                // Rede voltou — enviar relatórios pendentes de falha de conectividade
                if (canReportConnectivity()) {
                    flushPendingConnectivityReports();
                }
                return response;
            },
            async (error) => {
                if (error?.config?.__skipConnectivityHandling) {
                    return Promise.reject(error);
                }

                const diagnosis = await diagnoseConnectivity(error);
                error.connectivityDiagnosis = diagnosis;

                // Retry único para 403: reduz falsos positivos por falhas transitórias de borda/rede.
                if (diagnosis.has403 && error?.config && !error.config.__retry403Done) {
                    error.config.__retry403Done = true;
                    return axios(error.config);
                }

                // Retries para erro de rede: reduz falso negativo por oscilação momentânea.
                if (diagnosis.networkError && !diagnosis.has403 && error?.config) {
                    const retryCount = error.config.__networkRetryCount || 0;
                    if (retryCount < 3) {
                        error.config.__networkRetryCount = retryCount + 1;
                        if (retryCount === 0) {
                            setApiState('retrying');
                        }
                        const delays = [0, 500, 1200];
                        const delay = delays[retryCount] || 0;
                        if (delay > 0) {
                            await new Promise(res => setTimeout(res, delay));
                        }
                        return axios(error.config);
                    } else {
                        setApiState('offline');
                    }
                }

                if (diagnosis.has403 && forbiddenRedirectGuard.register403()) {
                    if (typeof connectivityHooks.onRejectedRoute === 'function') {
                        connectivityHooks.onRejectedRoute(diagnosis, error);
                    }
                } else if (diagnosis.networkError && !diagnosis.has403 && canReportConnectivity()) {
                    await reportConnectivityEvent('connection', diagnosis, error);
                } else if (!diagnosis.has403) {
                    forbiddenRedirectGuard.reset();
                }

                return Promise.reject(error);
            }
        );
    },

    removeHeader() {
        axios.defaults.headers.common = {}
    },
    get(apiPath) {
        return axios.get(apiPath)
    },

    download(apiPath) {
        return axios({
			url: apiPath,
			method: 'GET',
			responseType: 'blob', // important
		})
    },
    post(apiPath, data) {
        return axios.post(apiPath, data);
	},

    put(apiPath, data) {
        return axios.put(apiPath, data)
	},

    delete(apiPath) {
        return axios.delete(apiPath)
    },

    async flushNetwork() {
        setApiState('retrying');
        try {
            await axios.get('/', {
                baseURL: axios.defaults.baseURL,
                __skipConnectivityHandling: true,
                headers: { 'Connection': 'close', 'X-Cache-Buster': Date.now() },
                params: { t: Date.now() },
                timeout: 5000
            });
            setApiState('online');
        } catch (e) {
            setApiState('offline');
        }
    },

    /**
     * Perform a custom Axios request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
    **/
    customRequest(data) {
        return axios(data)
    },
    axios() {
        return axios;
    },
}
